import dayjs from "dayjs"

import {proxyService} from "../../services/Interceptor"
import {API_URL} from "../../config"

// Couche réseau des routes /account/* : passe par le BFF same-origin via
// proxyService (baseURL "/api"), en préfixant "/account". Le cookie de session
// part tout seul ; le BFF le gère (pose au login, efface au logout, Bearer).
const Acc = {
    get: (path, params, config) => proxyService.request({url: "/account" + path, method: "get", params, ...config}),
    post: (path, data, config) => proxyService.request({url: "/account" + path, method: "post", data, ...config}),
}

// URL absolue d'un PDF reçu/cerfa (routes publiques /v1/recus|cerfas/:id).
export function recuUrl(id) { return API_URL + "/recus/" + id }
export function cerfaUrl(id) { return API_URL + "/cerfas/" + id }

// ─────────────────────────────────────────────────────────────────────
//  Couche API de l'Espace de gestion de compte (AccountSpace).
//
//  - La session est portée par un cookie HttpOnly posé/lu par le BFF Next
//    same-origin (proxyService + pages/api/[...path].js).
//    Le token n'est PLUS exposé au JS navigateur (anti-XSS).
//  - L'état "connecté" se déduit d'un appel /me (le cookie part tout seul).
//  - Les routes non authentifiées reçoivent l'org via `identifier` dans le body
//    (résolu côté API par resolveOrgByIdentifier : id/prefix/domain/virtuals).
// ─────────────────────────────────────────────────────────────────────

export const ACCOUNT_TOKEN_KEY = "ACCOUNT_TOKEN"

// ── Compat des anciens call-sites ────────────────────────────────────────
//  getToken/setToken/clearToken ne pilotent PLUS la session (c'est le cookie
//  HttpOnly). On les conserve pour ne casser aucun import existant (index.js,
//  AuthScreen.js, WebEditor/form.js, paiement/index.js) :
//   - getToken() est utilisé comme « suis-je connecté ? » → on délègue au cookie
//     non lisible en JS ; on ne peut donc plus répondre depuis localStorage. Les
//     écrans concernés appellent /me de toute façon ; getToken() reste donc une
//     heuristique best-effort (présence d'un flag non-sensible posé au login).
//   - setToken/clearToken deviennent des no-op (transition : on n'écrit plus de
//     token en clair). Le nettoyage d'un éventuel ancien token localStorage est
//     fait une fois ci-dessous.
const ACCOUNT_HINT_KEY = "ACCOUNT_LOGGED_IN"

export function getToken() {
    if (typeof window === "undefined") return null
    try {
        // Hint non-sensible (pas le token) pour éviter un flash avant /me.
        return window.localStorage?.getItem(ACCOUNT_HINT_KEY) ? "1" : null
    } catch (_) { return null }
}

export function setToken(_token) {
    // Plus de token en clair : on pose seulement un hint « connecté ».
    if (typeof window === "undefined") return
    try {
        window.localStorage?.setItem(ACCOUNT_HINT_KEY, "1")
        window.localStorage?.removeItem(ACCOUNT_TOKEN_KEY) // purge legacy
    } catch (_) {}
}

export function clearToken() {
    if (typeof window === "undefined") return
    try {
        window.localStorage?.removeItem(ACCOUNT_HINT_KEY)
        window.localStorage?.removeItem(ACCOUNT_TOKEN_KEY) // purge legacy
    } catch (_) {}
}

// `identifier` = comment l'API retrouve l'org (on utilise org.id, matché en
// premier par resolveOrgByIdentifier).
export function resolveIdentifier(org) {
    return org?.id || org?.prefix || org?.domain || null
}

// Transforme la soumission du formulaire d'inscription (format natif forms_submit :
// data.identity + champs custom préfixés [TYPE]_) en `registration` attendu par
// verify-code. L'identité FULL porte l'adresse dans data.identity → on la replie
// en profile.adresses[]. Les champs custom restent au format natif dans extras.
const IDENTITY_KEYS = ["homme", "nom", "prenom", "email", "tel", "adresse", "postal", "ville", "pays", "etat", "note_adresse"]

export function buildRegistrationFromSubmit(data = {}) {
    const identity = data.identity || {}

    // Extras = toutes les clés de data hors `identity` et `extra` (champs custom du bloc).
    const extras = {}
    for (const k of Object.keys(data)) {
        if (k === "identity" || k === "extra" || k === "__paiementMethod") continue
        extras[k] = data[k]
    }

    const registration = {
        nom: identity.nom,
        prenom: identity.prenom,
        email: identity.email,
        tel: identity.tel,
        homme: identity.homme,
    }

    // Adresse (mode FULL) → entrée adresses[].
    if (identity.adresse || identity.ville || identity.postal) {
        registration.adresses = [{
            type: "PERSO",
            adresse: identity.adresse || null,
            postal: identity.postal || null,
            ville: identity.ville || null,
            pays: identity.pays || "France",
            etat: identity.etat || null,
        }]
    }

    if (Object.keys(extras).length > 0) {
        registration.extras = extras
    }

    return registration
}

// ─────────────────────────────────────────────────────────────────────
//  Fonction INVERSE de buildRegistrationFromSubmit : transforme un `profile`
//  stocké (identité + `extras` au format natif {"[TYPE]_clé":valeur}) en
//  `initialValues` pour le <Form> antd rendu par AccountEditorRender.
//
//  C'est ce qui permet au panneau « Mes informations » de pré-remplir À LA FOIS
//  l'identité ET les champs custom du bloc → le visiteur peut les MODIFIER.
//
//  Chaque type de champ a un format d'init précis (cf. plugins formPlugins/) :
//    TEXT/SELECT/CHECKBOX/DOCUMENT(_MULTI)  → valeur telle quelle
//    NUMBER                                  → Number()
//    SELECT_MULTI ("a,b")                    → ["a","b"] (Select mode multiple)
//    DATE                                    → Date JS (DdnField)
//    DATETIME/YEAR/MONTH/TIME                → dayjs (DatePicker antd v5)
//  Fail-safe : toute conversion qui échoue laisse le champ vide (jamais de throw).
// ─────────────────────────────────────────────────────────────────────
export function buildInitialValuesFromProfile(profile = {}) {
    const initialValues = {
        identity: {
            homme: profile.homme,
            prenom: profile.prenom,
            nom: profile.nom,
            email: profile.email,
            tel: profile.tel,
        },
    }

    const extras = (profile.extras && typeof profile.extras === "object") ? profile.extras : {}
    for (const key of Object.keys(extras)) {
        const value = extras[key]
        if (value === undefined || value === null) continue
        try {
            if (key.startsWith("[NUMBER]_")) {
                const n = Number(value)
                if (!Number.isNaN(n)) initialValues[key] = n
            } else if (key.startsWith("[CHECKBOX]_")) {
                initialValues[key] = value === true || value === "true"
            } else if (key.startsWith("[SELECT_MULTI]_")) {
                if (Array.isArray(value)) {
                    initialValues[key] = value
                } else if (typeof value === "string") {
                    initialValues[key] = value.split(",").map(v => v.trim()).filter(Boolean)
                }
            } else if (key.startsWith("[DATE]_")) {
                const d = new Date(value)
                if (!Number.isNaN(d.getTime())) initialValues[key] = d
            } else if (key.startsWith("[DATETIME]_") || key.startsWith("[YEAR]_") || key.startsWith("[MONTH]_") || key.startsWith("[TIME]_")) {
                const d = dayjs(value)
                if (d.isValid()) initialValues[key] = d
            } else {
                // TEXT, SELECT, DOCUMENT, DOCUMENT_MULTI, et tout type inconnu → tel quel.
                initialValues[key] = value
            }
        } catch (_) { /* champ ignoré, jamais de throw qui casserait tout le form */ }
    }

    return initialValues
}

// Tous les appels passent par le BFF same-origin (proxyService, baseURL /api),
// qui gère le cookie de session. Le préfixe "/account" est porté par Acc.
export const AccountAPI = {
    registrationForm(identifier) {
        return Acc.get("/registration-form", {identifier})
    },
    requestCode(identifier, email, lang) {
        return Acc.post("/auth/request-code", {identifier, email, lang})
    },
    verifyCode(identifier, email, code, registration, lang) {
        return Acc.post("/auth/verify-code", {identifier, email, code, registration, lang})
    },
    me() {
        // Sans session, /me renvoie 401/403 = « pas connecté » : ce n'est PAS
        // une vraie erreur (on retombe juste sur l'écran d'auth). On marque
        // l'appel `silentError` → l'intercepteur ne déclenche aucune notification.
        return Acc.get("/me", undefined, {silentError: true})
    },
    editProfile(profile) {
        return Acc.post("/me/edit", {profile})
    },
    addAddress(address) {
        return Acc.post("/me/address", {address})
    },
    family() {
        return Acc.get("/family")
    },
    addMember(payload) {
        return Acc.post("/family/add", payload)
    },
    removeMember(id) {
        return Acc.post("/family/remove", {id})
    },
    dashboard() {
        return Acc.get("/dashboard")
    },
    payLinkPromesse(id) {
        return Acc.post("/promesses/" + id + "/pay-link", {})
    },
    payLinkGroup({contactId, assoc,devise, occasion} = {}) {
        return Acc.post("/promesses/pay-link-group", {contactId, assoc,devise, occasion})
    },
    receiptYears() {
        return Acc.get("/receipts/years")
    },
    exportReceipts(year) {
        return Acc.post("/receipts/export", {year})
    },
    logout() {
        return Acc.get("/logout")
    },
}
